import { ButtonGroup, Colors, Dialog } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import React from "react";

import { DogDnaApi, DogDnaMarkersGridDto, DogDnaSearch, EIsagNorm } from "../../../../../api";
import { AddButton, CustomDataTable, EditButton, ExportButton } from "../../../../../components";
import { useEventsContext, useGlobalData } from "../../../../../contexts";
import { useApiService, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { DnaMarkerEdit } from "./DnaMarkerEdit";
import { DnaMarkerSaveAllPanel } from "./DnaMarkerSaveAllPanel";
import { styled } from "styled-components";

export interface IDogDnaMarkersListProps {
  dogId: number;
  dogMarkerId: number;
  isag: EIsagNorm;
}

const DnaDialogContainer = styled.div`
  padding-bottom: 0.5rem;
`;

export const DogDnaMarkersList: React.FunctionComponent<IDogDnaMarkersListProps> = ({ dogId, dogMarkerId, isag }) => {
  const { t } = useTl();
  const api = useApiService(DogDnaApi);

  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState({
    serverMode: true,
    enablePagination: false,
    enableFilter: true,
    availablePageSizes: [25],
    pageSize: 25,
    sortKeys: { marker: "ASC" },
  });

  const { currentLanguage } = useGlobalData();
  const [editedRowId, seteditedRowId] = React.useState<number>();

  const [IsDialogOpened, setIsDialogOpened] = React.useState(false);
  const searchFunction = React.useCallback(
    (sObj: DogDnaSearch) => {
      setsobjState(DogDnaSearch.fromJS({ ...sObj, dogId: dogId, isag: isag }));
      return api.baseSearch(DogDnaSearch.fromJS({ ...sObj, dogId: dogId, isag: isag }));
    },
    [api, dogId]
  );

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const OnRefreshSearchTablesEvent = React.useCallback(() => {
    search();
  }, [search]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RefreshSearchTables", OnRefreshSearchTablesEvent);
    return () => unsubscribeEvent("RefreshSearchTables", OnRefreshSearchTablesEvent);
  }, [OnRefreshSearchTablesEvent, subscribeToEvent, unsubscribeEvent]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => "",
        fieldName: "_action",
        alignment: "center",
        autoFitContent: true,
        render: (row: DogDnaMarkersGridDto) => {
          return <EditButton onClick={() => seteditedRowId(row.dnaMarkerResultId)} />;
        },
      },
      {
        header: () => t(ETLCodes.Marker),
        fieldName: "marker",
      },
      {
        header: () => t(ETLCodes.Value1),
        fieldName: "value1",
      },
      {
        header: () => t(ETLCodes.Value2),
        fieldName: "value2",
      },
    ],
    [t]
  );

  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  const onRowEditSave = React.useCallback(() => {
    seteditedRowId(null);
    search();
  }, [search]);

  const onSave = React.useCallback(() => {
    setIsDialogOpened(false);
    search();
  }, [search]);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.DnaMarkers)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {!!dogMarkerId && !tableState.totalCount ? (
              <AddButton text={t(ETLCodes.AddDnaMarkers)} onClick={() => setIsDialogOpened(true)} />
            ) : null}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>

      <Dialog
        title={t(ETLCodes.DnaMarkers)}
        isOpen={!!editedRowId}
        onClose={() => seteditedRowId(null)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 800, height: 230, background: Colors.WHITE }}
      >
        <DnaMarkerEdit dnaMarkerResultId={editedRowId} onSave={onRowEditSave} />
      </Dialog>

      <Dialog
        title={t(ETLCodes.DnaMarkers)}
        isOpen={IsDialogOpened}
        onClose={() => setIsDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 800, background: Colors.WHITE }}
      >
        <DnaDialogContainer>
          <DnaMarkerSaveAllPanel onSave={onSave} dogId={dogId} />
        </DnaDialogContainer>
      </Dialog>
    </>
  );
};
